"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import { logger } from "@/utils/logger";

interface GoogleAnalyticsProps {
  gaId: string;
}

declare global {
  interface Window {
    gtag: (
      command: "config" | "event" | "js" | "set",
      targetId: string | Date | Record<string, any>,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

export default function GoogleAnalytics({ gaId }: GoogleAnalyticsProps) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const shouldLoadRef = useRef(false);
  const lastConsentValueRef = useRef<string | null>(null);

  useEffect(() => {
    // Analytics is essential for tracking purchases and business metrics
    // Check if user has consented to necessary cookies (which includes analytics)
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const enableAnalytics = (
      reason: string,
      metadata?: Record<string, unknown>
    ) => {
      if (shouldLoadRef.current) {
        return;
      }

      shouldLoadRef.current = true;
      setShouldLoad(true);
      logger.info("GA loading enabled", {
        component: "GoogleAnalytics",
        reason,
        ...metadata,
      });

      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const checkConsent = (
      source: "initial" | "storage" | "interval" = "initial"
    ) => {
      if (shouldLoadRef.current) {
        return;
      }

      try {
        const consent = localStorage.getItem("cookie-consent");
        if (consent !== lastConsentValueRef.current) {
          lastConsentValueRef.current = consent;
          logger.debug("GA consent check triggered", {
            component: "GoogleAnalytics",
            hasConsent: !!consent,
            consentValue: consent,
            source,
          });
        }

        if (consent) {
          const preferences = JSON.parse(consent);
          // Analytics is now part of necessary cookies, so check for necessary or analytics
          if (
            preferences.necessary === true ||
            preferences.analytics === true
          ) {
            enableAnalytics("consent-granted", { preferences });
            return;
          }
        }
        // If no consent exists yet, still load analytics (it's essential)
        // The consent banner will appear and set it properly
        enableAnalytics("essential-fallback");
      } catch (error) {
        // If parsing fails, still load analytics (it's essential)
        logger.warn(
          "Failed to parse cookie consent, enabling GA by default",
          {
            component: "GoogleAnalytics",
          },
          error instanceof Error ? error : new Error(String(error))
        );
        enableAnalytics("consent-parse-failure");
      }
    };

    // Check immediately
    checkConsent("initial");

    // Listen for storage changes (when user accepts/rejects consent)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "cookie-consent") {
        logger.debug("GA storage event detected for cookie-consent", {
          component: "GoogleAnalytics",
        });
        checkConsent();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also check periodically in case consent was set in same window
    intervalId = setInterval(() => {
      if (shouldLoadRef.current) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }

      logger.debug("GA periodic consent re-check pending consent", {
        component: "GoogleAnalytics",
      });
      checkConsent("interval");
    }, 4000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  // Only load GA in production
  if (process.env.NODE_ENV !== "production") {
    if (process.env.NODE_ENV === "development") {
      logger.debug("Google Analytics script not loaded (not in production)", {
        component: "GoogleAnalytics",
        gaId,
      });
    }
    return null;
  }

  if (!gaId) {
    logger.warn("Google Analytics ID not provided - analytics disabled", {
      component: "GoogleAnalytics",
    });
    return null;
  }

  // Analytics is essential - always load if GA ID is provided
  if (!shouldLoad) {
    logger.debug("GA shouldLoad=false, deferring script injection", {
      component: "GoogleAnalytics",
    });
    return null;
  }

  logger.debug("GA scripts being injected", {
    component: "GoogleAnalytics",
    gaId,
  });

  return (
    <>
      {/* Load the GA script - lazy loaded for better performance */}
      <Script
        strategy="lazyOnload"
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
      />

      {/* Initialize GA - optimized for performance with proper cookie configuration */}
      <Script
        id="google-analytics-init"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            // Configure GA with proper cookie settings to prevent "expires" overwrite warnings
            gtag('config', '${gaId}', {
              cookie_flags: 'max-age=7200;secure;samesite=none',
              cookie_expires: 63072000, // 2 years in seconds
              anonymize_ip: true
            });
          `,
        }}
      />
    </>
  );
}
